import { create } from 'zustand';

/**
 * OAuth 연결 다이얼로그 상태 스토어
 *
 * 특정 에이전트 실행 시 OAuth 연결이 필요한 경우
 * 다이얼로그를 표시하고 연결 후 콜백을 실행합니다.
 */
interface OAuthDialogState {
  isOpen: boolean;
  provider: 'google' | 'naver' | null;
  title: string;
  description: string;
  callbackURL: string;
}

interface OAuthDialogActions {
  openDialog: (params: {
    provider: 'google' | 'naver';
    title?: string;
    description?: string;
    callbackURL?: string;
  }) => void;
  closeDialog: () => void;
}

type OAuthDialogStore = OAuthDialogState & OAuthDialogActions;

const defaultState: OAuthDialogState = {
  isOpen: false,
  provider: null,
  title: '',
  description: '',
  callbackURL: '',
};

export const useOAuthDialogStore = create<OAuthDialogStore>()((set) => ({
  ...defaultState,

  openDialog: ({ provider, title, description, callbackURL }) =>
    set({
      isOpen: true,
      provider,
      title: title ?? getDefaultTitle(provider),
      description: description ?? getDefaultDescription(provider),
      callbackURL: callbackURL ?? window.location.pathname,
    }),

  closeDialog: () => set(defaultState),
}));

function getDefaultTitle(provider: 'google' | 'naver'): string {
  switch (provider) {
    case 'google':
      return 'Google 계정 연결 필요';
    case 'naver':
      return '네이버 계정 연결 필요';
  }
}

function getDefaultDescription(provider: 'google' | 'naver'): string {
  switch (provider) {
    case 'google':
      return '이 에이전트를 사용하려면 Google 계정 연결이 필요합니다. Google 계정을 연결하시겠습니까?';
    case 'naver':
      return '이 에이전트를 사용하려면 네이버 계정 연결이 필요합니다. 네이버 계정을 연결하시겠습니까?';
  }
}

/**
 * 컴포넌트 외부에서 다이얼로그 열기
 */
export function openOAuthDialog(params: Parameters<OAuthDialogActions['openDialog']>[0]): void {
  useOAuthDialogStore.getState().openDialog(params);
}

/**
 * 컴포넌트 외부에서 다이얼로그 닫기
 */
export function closeOAuthDialog(): void {
  useOAuthDialogStore.getState().closeDialog();
}
